import { SelfElectedStateFundingProgramInput } from "@outschool/gql-backend-generated";

export function shouldRequireMembershipForEnrollmentBasedOnSelfElectedStateFundingProgram(
  selfElectedStateFundingProgram:
    | SelfElectedStateFundingProgramInput
    | null
    | undefined,
): boolean {
  return (
    !selfElectedStateFundingProgram?.fundingOptions ||
    [
      // used in v1
      selfElectedStateFundingProgram?.fundingOptions?.charter,
      selfElectedStateFundingProgram?.fundingOptions?.statePortal,
      selfElectedStateFundingProgram?.fundingOptions?.reimbursement,
      // used in v2
      selfElectedStateFundingProgram?.fundingOptions?.otherProgramName,
      selfElectedStateFundingProgram?.fundingOptions?.stateDebitCard,
      selfElectedStateFundingProgram?.fundingOptions?.stateProgramName,
    ].every(option => !Boolean(option))
  );
}

export const userUsesSelfElectedPublicFundingOptions = (
  selfElectedStateFundingProgram:
    | SelfElectedStateFundingProgramInput
    | null
    | undefined,
): boolean => {
  return (
    !!selfElectedStateFundingProgram?.fundingOptions &&
    [
      // used in v1
      selfElectedStateFundingProgram?.fundingOptions?.charter,
      selfElectedStateFundingProgram?.fundingOptions?.statePortal,
      selfElectedStateFundingProgram?.fundingOptions?.reimbursement,
      // used in v2
      selfElectedStateFundingProgram?.fundingOptions?.stateDebitCard,
      selfElectedStateFundingProgram?.fundingOptions?.otherProgramName,
      selfElectedStateFundingProgram?.fundingOptions?.stateProgramName,
    ].some(option => Boolean(option))
  );
};

export function wasFundingProgramSelected(
  selfElectedStateFundingProgram:
    | SelfElectedStateFundingProgramInput
    | null
    | undefined,
): boolean {
  if (
    selfElectedStateFundingProgram?.fundingOptions &&
    [
      // used in v1
      selfElectedStateFundingProgram?.fundingOptions?.charter,
      selfElectedStateFundingProgram?.fundingOptions?.statePortal,
      selfElectedStateFundingProgram?.fundingOptions?.reimbursement,
      selfElectedStateFundingProgram?.fundingOptions?.selfPay,
      // used in v2
      selfElectedStateFundingProgram?.fundingOptions?.otherProgramName,
      selfElectedStateFundingProgram?.fundingOptions?.stateProgramName,
      selfElectedStateFundingProgram?.fundingOptions?.stateDebitCard,
    ].some(option => Boolean(option))
  ) {
    return true;
  }
  return false;
}

// Shared constants for public funding components
export const US_STATES = [
  { name: "Alabama", abbreviation: "AL" },
  { name: "Alaska", abbreviation: "AK" },
  { name: "Arizona", abbreviation: "AZ" },
  { name: "Arkansas", abbreviation: "AR" },
  { name: "California", abbreviation: "CA" },
  { name: "Colorado", abbreviation: "CO" },
  { name: "Connecticut", abbreviation: "CT" },
  { name: "Delaware", abbreviation: "DE" },
  { name: "Florida", abbreviation: "FL" },
  { name: "Georgia", abbreviation: "GA" },
  { name: "Hawaii", abbreviation: "HI" },
  { name: "Idaho", abbreviation: "ID" },
  { name: "Illinois", abbreviation: "IL" },
  { name: "Indiana", abbreviation: "IN" },
  { name: "Iowa", abbreviation: "IA" },
  { name: "Kansas", abbreviation: "KS" },
  { name: "Kentucky", abbreviation: "KY" },
  { name: "Louisiana", abbreviation: "LA" },
  { name: "Maine", abbreviation: "ME" },
  { name: "Maryland", abbreviation: "MD" },
  { name: "Massachusetts", abbreviation: "MA" },
  { name: "Michigan", abbreviation: "MI" },
  { name: "Minnesota", abbreviation: "MN" },
  { name: "Mississippi", abbreviation: "MS" },
  { name: "Missouri", abbreviation: "MO" },
  { name: "Montana", abbreviation: "MT" },
  { name: "Nebraska", abbreviation: "NE" },
  { name: "Nevada", abbreviation: "NV" },
  { name: "New Hampshire", abbreviation: "NH" },
  { name: "New Jersey", abbreviation: "NJ" },
  { name: "New Mexico", abbreviation: "NM" },
  { name: "New York", abbreviation: "NY" },
  { name: "North Carolina", abbreviation: "NC" },
  { name: "North Dakota", abbreviation: "ND" },
  { name: "Ohio", abbreviation: "OH" },
  { name: "Oklahoma", abbreviation: "OK" },
  { name: "Oregon", abbreviation: "OR" },
  { name: "Pennsylvania", abbreviation: "PA" },
  { name: "Rhode Island", abbreviation: "RI" },
  { name: "South Carolina", abbreviation: "SC" },
  { name: "South Dakota", abbreviation: "SD" },
  { name: "Tennessee", abbreviation: "TN" },
  { name: "Texas", abbreviation: "TX" },
  { name: "Utah", abbreviation: "UT" },
  { name: "Vermont", abbreviation: "VT" },
  { name: "Virginia", abbreviation: "VA" },
  { name: "Washington", abbreviation: "WA" },
  { name: "West Virginia", abbreviation: "WV" },
  { name: "Wisconsin", abbreviation: "WI" },
  { name: "Wyoming", abbreviation: "WY" },
] as const;

export const FUNDING_OPTION_KEYS = {
  stateProgramName: "stateProgramName" as const,
  otherProgramName: "otherProgramName" as const,
  stateDebitCard: "stateDebitCard" as const,
  statePortal: "statePortal" as const,
  reimbursement: "reimbursement" as const,
  charter: "charter" as const,
  selfPay: "selfPay" as const,
} as const;

export type FundingOptionKey = keyof typeof FUNDING_OPTION_KEYS;
